import React, { useCallback } from 'react';
import { useSetPatientSelfPayMutation } from '@features/patient-coverages/store';
import { useSubjectLanguageVariants } from '@features/user';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import SentryService from '@services/SentryService';
import { useSnackbar } from 'notistack';

import type { ClientErrorResponse } from '../types';

interface UseSelfPayCoverageDesignationArgs {
  patientId: string;
  /**
   * Patient-level self-pay flag — the SOLE source of designation state
   * since the GDN-3177 contract phase. Coverage rows neither imply nor
   * preclude it: a designated patient may hold zero rows or live
   * insurance (BE #4223 made designate-while-insured first-class). Feed
   * it from `useOrganizedPatientCoverages(patientId).isSelfPay`.
   */
  patientIsSelfPay?: boolean | null;
  refetchCoverages: () => Promise<unknown> | unknown;
  onCoverageSave?: () => void;
  onAfterSave?: () => void;
}

// Designation writes the patient, not the coverage table (GDN-3068):
// PUT /v2/provider/patients/{id}/self-pay, then re-fetch coverages and
// render whatever comes back. Post-contract (GDN-3177) unchecking sets
// the flag false and creates nothing — the editor's empty state takes
// over.
export function useSelfPayCoverageDesignation({
  patientId,
  patientIsSelfPay,
  refetchCoverages,
  onCoverageSave,
  onAfterSave,
}: UseSelfPayCoverageDesignationArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const [setPatientSelfPay, { isLoading: isSaving }] = useSetPatientSelfPayMutation();
  const isSelfPay = Boolean(patientIsSelfPay);

  const setDesignation = useCallback(
    async (nextIsSelfPay: boolean) => {
      try {
        await setPatientSelfPay({
          patientId,
          selfPayDesignationRequest: { is_self_pay: nextIsSelfPay },
        }).unwrap();

        onAfterSave?.();
        await refetchCoverages();
        onCoverageSave?.();
      } catch (error) {
        // 409 coverage_order_conflict: the GDN-3043 slot rule, RETIRED
        // by BE #4223 (designate-while-insured now succeeds). Kept as a
        // defensive fallback for a backend that still enforces it — a
        // business-rule conflict, not an exception: surface the message
        // and leave the checkbox where the server says it is.
        const errorData = (error as { data?: ClientErrorResponse })?.data;
        if (errorData?.error_code === 'coverage_order_conflict') {
          enqueueSnackbar(
            errorData.message ||
              `This ${subjectLanguageVariants.singular} has active insurance in the primary slot. Remove or expire it before marking them self-pay.`,
            { variant: 'warning' },
          );
          return;
        }
        SentryService.captureException(error, {
          level: 'error',
          tags: {
            patient_id: patientId,
            action: nextIsSelfPay ? 'mark_self_pay' : 'clear_self_pay',
          },
          extra: { error },
        });
        enqueueSnackbar(
          nextIsSelfPay ? 'Failed to mark self-pay' : 'Failed to switch to insurance',
          { variant: 'error' },
        );
      }
    },
    [
      enqueueSnackbar,
      onAfterSave,
      onCoverageSave,
      patientId,
      refetchCoverages,
      setPatientSelfPay,
      subjectLanguageVariants.singular,
    ],
  );

  const markSelfPay = useCallback(() => setDesignation(true), [setDesignation]);
  const clearSelfPay = useCallback(() => setDesignation(false), [setDesignation]);

  const handleSelfPayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      void setDesignation(event.target.checked);
    },
    [setDesignation],
  );

  return {
    isSelfPay,
    isSaving,
    handleSelfPayChange,
    markSelfPay,
    clearSelfPay,
  };
}

interface SelfPayCoverageToggleProps extends UseSelfPayCoverageDesignationArgs {
  description?: React.ReactNode;
  descriptionVariant?: TypographyProps['variant'];
  containerSx?: SxProps<Theme>;
  descriptionSx?: SxProps<Theme>;
}

export function SelfPayCoverageToggle({
  description,
  descriptionVariant = 'body2',
  containerSx,
  descriptionSx,
  ...designationArgs
}: SelfPayCoverageToggleProps) {
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const { isSelfPay, isSaving, handleSelfPayChange } =
    useSelfPayCoverageDesignation(designationArgs);

  return (
    <Box sx={containerSx}>
      <FormControlLabel
        // The checkbox is non-optimistic (checked state comes from the
        // server cache), so hold it while the PUT + refetch are in
        // flight — racing writes could otherwise land out of order.
        control={
          <Checkbox checked={isSelfPay} onChange={handleSelfPayChange} disabled={isSaving} />
        }
        label={`${subjectLanguageVariants.singularCap} is self-pay`}
      />
      <Typography
        variant={descriptionVariant}
        color="text.secondary"
        sx={{ ml: 4, ...descriptionSx }}
      >
        {description ??
          `Use this only when this ${subjectLanguageVariants.singular} should be billed directly instead of submitting to insurance.`}
      </Typography>
    </Box>
  );
}

interface SelfPayCoverageCardProps {
  subjectLabel?: string;
  description?: React.ReactNode;
  descriptionVariant?: TypographyProps['variant'];
}

export function SelfPayCoverageCard({
  subjectLabel,
  description,
  descriptionVariant = 'body2',
}: SelfPayCoverageCardProps) {
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const resolvedSubjectLabel = subjectLabel ?? subjectLanguageVariants.singular;

  return (
    <Alert severity="info" sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Self-pay
      </Typography>
      <Typography variant={descriptionVariant} color="text.secondary">
        {description ??
          `This ${resolvedSubjectLabel} is designated as self-pay. Claims are not submitted and payer eligibility prompts stay quiet while this designation is on — even when insurance is on file.`}
      </Typography>
    </Alert>
  );
}
